/**
 * Organism · Demo — drives the mock player.
 *
 * The bar is the ORIGINAL video, exactly as YouTube's own is. FitCutter does
 * not re-cut anything: it seeks past what the viewer excluded and shortens the
 * pauses. So the six tagged markers keep fixed positions and widths for the
 * life of the section. A preference changes the playhead, never the bar.
 *
 * An earlier version drew the personalised timeline instead. It made markers
 * move and collapse to hairlines as you ticked things, which is both unlike
 * the extension and impossible to read.
 *
 * Playback is simulated. There is no video element, so nothing buffers and the
 * section is interactive from first paint.
 */

/**
 * `untagged` is the rest of the video — the parts the creator never tagged.
 * It plays, it takes up runtime, and it draws nothing on the bar, which is what
 * spaces the six tagged blocks out instead of paving the whole timeline.
 */
type SegmentKind = 'exercise' | 'rest' | 'untagged';

interface Segment {
  kind: SegmentKind;
  /** Seconds in the creator's original cut. */
  seconds: number;
  /** Movement name, for segments a viewer can skip. */
  move?: string;
  /** Equipment the segment needs, if any. */
  equipment?: string;
}

/**
 * The creator's tagged cut: rest, an equipment exercise, a movement — twice.
 *
 * Every chip in the panel owns exactly one segment, and no segment answers to
 * two chips: an exercise is filtered by its equipment or by its name, never
 * both, so the cause of a skip is never ambiguous. The equipment exercises are
 * therefore named for the movement, not the kit — "Kettlebell Swing" is filtered
 * by the Kettlebell chip, not by a movement chip of the same name.
 *
 * Six tagged blocks of equal length, evenly spaced along the video with
 * untagged runtime between them. The bar therefore reads as markers on a
 * timeline rather than a paved strip, and because the untagged parts always
 * play, filtering everything out can never leave nothing to watch.
 *
 * It opens on a rest deliberately. The scrim is the first thing FitCutter does
 * that a creator can see, and burying it behind a warm-up made the section feel
 * dead on arrival.
 */
const TAG_SECONDS = 20;
const GAP_SECONDS = 40;
const EDGE_SECONDS = 20;

const gap = (seconds: number): Segment => ({ kind: 'untagged', seconds });

const CUT: Segment[] = [
  gap(EDGE_SECONDS),
  { kind: 'rest', seconds: TAG_SECONDS },
  gap(GAP_SECONDS),
  { kind: 'exercise', seconds: TAG_SECONDS, move: 'Kettlebell Swing', equipment: 'Kettlebell' },
  gap(GAP_SECONDS),
  { kind: 'exercise', seconds: TAG_SECONDS, move: 'Squat' },
  gap(GAP_SECONDS),
  { kind: 'rest', seconds: TAG_SECONDS },
  gap(GAP_SECONDS),
  { kind: 'exercise', seconds: TAG_SECONDS, move: 'Overhead Press', equipment: 'Dumbbell' },
  gap(GAP_SECONDS),
  { kind: 'exercise', seconds: TAG_SECONDS, move: 'Plank' },
  gap(EDGE_SECONDS),
];

/** Where each segment starts in the original video, and how long the whole is. */
const STARTS: number[] = [];
const TOTAL = CUT.reduce((sum, segment) => {
  STARTS.push(sum);
  return sum + segment.seconds;
}, 0);

/**
 * Seconds a rest is shortened to, per preference. `null` keeps the creator's.
 * These are the extension's own choices.
 */
const REST_CHOICES: Record<string, number | null> = {
  creator: null,
  '10': 10,
  '5': 5,
  '0': 0,
};

/** The option the panel ships on. */
const DEFAULT_REST = '5';

/** Video-seconds per real second while skimming ordinary footage. */
const RATE_SKIM = 14;

/** How long a skip notice stays up, in real milliseconds. */
const SKIP_NOTICE_MS = 4200;

function formatClock(totalSeconds: number): string {
  const whole = Math.max(0, Math.round(totalSeconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

export function initDemo(root?: HTMLElement | null): () => void {
  const demo = root ?? document.querySelector<HTMLElement>('[data-fc-demo]');
  if (!demo) return () => {};

  const track = demo.querySelector<HTMLElement>('.fc-player__track');
  const head = demo.querySelector<HTMLElement>('.fc-player__head');
  const time = demo.querySelector<HTMLElement>('.fc-player__time');
  if (!track || !head || !time) return () => {};

  const trackEl: HTMLElement = track;
  const headEl: HTMLElement = head;
  const timeEl: HTMLElement = time;
  const demoEl: HTMLElement = demo;

  const poster = demo.querySelector<HTMLElement>('.fc-player__poster');
  const toggle = demo.querySelector<HTMLButtonElement>('.fc-player__toggle');
  const toggleLabel = demo.querySelector<HTMLElement>('[data-fc-toggle-label]');
  const rest = demo.querySelector<HTMLElement>('.fc-rest');
  const restTime = demo.querySelector<HTMLElement>('.fc-rest__time');
  const restFill = demo.querySelector<HTMLElement>('.fc-rest__fill');
  const restSkip = demo.querySelector<HTMLButtonElement>('.fc-rest__skip');
  const badge = demo.querySelector<HTMLElement>('.fc-brand-badge');
  const mini = demo.querySelector<HTMLElement>('.fc-brand-mini');

  const cleanups: Array<() => void> = [];
  const on = <K extends keyof HTMLElementEventMap>(
    el: EventTarget, type: K, fn: (event: HTMLElementEventMap[K]) => void,
  ) => {
    el.addEventListener(type, fn as EventListener);
    cleanups.push(() => el.removeEventListener(type, fn as EventListener));
  };

  // ─── Viewer preferences ───
  let restChoice = DEFAULT_REST;

  // The markup is the source of truth for what starts excluded, so a chip that
  // ships pressed and the bar it implies can never disagree.
  const excludedEquipment = new Set<string>();
  const excludedMoves = new Set<string>();
  demoEl.querySelectorAll<HTMLButtonElement>('.fc-chip[aria-pressed="true"]').forEach((chip) => {
    if (chip.dataset.fcEquipment) excludedEquipment.add(chip.dataset.fcEquipment);
    else if (chip.dataset.fcMove) excludedMoves.add(chip.dataset.fcMove);
  });

  /** The chip that removes this segment, if any. */
  function causeFor(segment: Segment): string | undefined {
    if (segment.move !== undefined && excludedMoves.has(segment.move)) return segment.move;
    if (segment.equipment !== undefined && excludedEquipment.has(segment.equipment)) return segment.equipment;
    return undefined;
  }

  /** Seconds this rest should actually last, given the preference. */
  function restSeconds(segment: Segment): number {
    // `restChoice` is read off a data attribute, so a typo in the markup would
    // otherwise reach the ticker as undefined and divide the rate into NaN.
    const known = Object.prototype.hasOwnProperty.call(REST_CHOICES, restChoice);
    const override = known ? REST_CHOICES[restChoice] : REST_CHOICES[DEFAULT_REST];
    return override === null ? segment.seconds : override;
  }

  /**
   * Whether the playhead will seek past this segment rather than play it.
   * Drives playback only — a marker's appearance is its kind, and does not
   * change with a preference.
   */
  function willJump(index: number): boolean {
    const segment = CUT[index];
    if (segment.kind === 'exercise') return causeFor(segment) !== undefined;
    if (segment.kind === 'rest') return restSeconds(segment) === 0;
    return false;
  }

  // ─── The bar ───
  //  Positions and colours come from the original cut and are set once here.
  //  Nothing below ever touches a marker again: the bar is the video, and a
  //  preference changes the playhead, not the video.
  const marks = new Map<number, HTMLElement>();
  CUT.forEach((segment, index) => {
    if (segment.kind === 'untagged') return;
    const mark = document.createElement('span');
    mark.className = `fc-player__segment fc-player__segment--${segment.kind}`;
    mark.style.left = `${(STARTS[index] / TOTAL) * 100}%`;
    mark.style.width = `${(segment.seconds / TOTAL) * 100}%`;
    trackEl.insertBefore(mark, headEl);
    marks.set(index, mark);
  });

  let elapsed = 0;

  /** Index of the segment the playhead sits in. */
  function segmentAt(at: number): number {
    for (let i = CUT.length - 1; i >= 0; i -= 1) if (at >= STARTS[i]) return i;
    return 0;
  }

  function paint(): void {
    // The markers say what the creator tagged, not what this viewer will see.
    // Their colour is the kind — green rest, amber exercise — and it does not
    // change with a preference: the bar is the video, and the video is the same
    // video either way. What a preference changes is the playhead, which jumps.
    headEl.style.width = `${(elapsed / TOTAL) * 100}%`;
    timeEl.textContent = `${formatClock(elapsed)} / ${formatClock(TOTAL)}`;

    const index = segmentAt(elapsed);
    const segment = CUT[index];
    const inRest = segment.kind === 'rest' && !willJump(index);

    if (rest) {
      rest.classList.toggle('is-visible', inRest);
      // The two overlays never share the picture. A skip cannot land inside a
      // rest under this model, but a notice raised just before one can still be
      // up when it starts — the scrim takes the screen back.
      if (inRest) hideNotice();
      if (inRest) {
        // The countdown is in REAL seconds — what the viewer waits — not in
        // video-seconds, which the rest preference has just compressed.
        const played = (elapsed - STARTS[index]) / segment.seconds;
        const remaining = restSeconds(segment) * (1 - played);
        if (restTime) restTime.textContent = String(Math.max(1, Math.ceil(remaining)));
        if (restFill) restFill.style.transform = `scaleX(${Math.max(0, 1 - played)})`;
      }
    }
  }

  // ─── Skip notices ───
  const notice = demoEl.querySelector<HTMLElement>('.fc-skip-notice');
  const noticeTitle = demoEl.querySelector<HTMLElement>('[data-fc-skip-title]');
  const noticeDetail = demoEl.querySelector<HTMLElement>('[data-fc-skip-detail]');
  const noticeUndo = demoEl.querySelector<HTMLButtonElement>('.fc-skip-notice__undo');
  let noticeTimer: number | null = null;
  let noticeCauses: string[] = [];
  /** The segment the notice is about, so Undo can seek back to its start. */
  let noticeIndex: number | null = null;

  function hideNotice(): void {
    notice?.classList.remove('is-visible');
    if (noticeTimer !== null) { window.clearTimeout(noticeTimer); noticeTimer = null; }
  }
  cleanups.push(hideNotice);

  function showSkip(index: number): void {
    const segment = CUT[index];
    const cause = causeFor(segment);
    if (!notice || cause === undefined) return;
    noticeCauses = [cause];
    noticeIndex = index;
    if (noticeTitle) noticeTitle.textContent = `Skipped — ${segment.move ?? cause}`;
    if (noticeDetail) {
      noticeDetail.textContent =
        `${formatClock(segment.seconds)} jumped · now at ${formatClock(STARTS[index] + segment.seconds)}`;
    }
    notice.classList.add('is-visible');
    if (noticeTimer !== null) window.clearTimeout(noticeTimer);
    noticeTimer = window.setTimeout(hideNotice, SKIP_NOTICE_MS);
  }

  function syncCounts(): void {
    demoEl.querySelectorAll<HTMLElement>('.fc-control-panel__group').forEach((group) => {
      const readout = group.querySelector('.fc-control-panel__count');
      if (!readout) return;
      const count = group.querySelectorAll('[aria-pressed="true"]').length;
      readout.textContent = count === 0 ? 'none off' : `${count} off`;
    });
  }

  // Undo releases the chip that caused the skip, so the preference and the bar
  // stay one fact rather than drifting.
  if (noticeUndo) {
    on(noticeUndo, 'click', () => {
      noticeCauses.forEach((tag) => { excludedMoves.delete(tag); excludedEquipment.delete(tag); });
      demoEl.querySelectorAll<HTMLButtonElement>('.fc-chip').forEach((chip) => {
        const value = chip.dataset.fcEquipment ?? chip.dataset.fcMove ?? '';
        if (noticeCauses.includes(value)) chip.setAttribute('aria-pressed', 'false');
      });
      syncCounts();

      // Take the part back, not just the preference: seek to where the segment
      // began and play it. Releasing the chip alone would leave the viewer past
      // the thing they just asked to see, which is the opposite of an undo.
      if (noticeIndex !== null) elapsed = STARTS[noticeIndex];
      hideNotice();
      paint();
      setPlaying(true);
    });
  }

  // ─── Playback ───
  let timer: number | null = null;

  function setPlaying(playing: boolean): void {
    if (toggleLabel) toggleLabel.textContent = playing ? 'Pause' : 'Play';
    toggle?.setAttribute('aria-pressed', String(playing));
    if (poster) poster.hidden = true;

    // The offer card stands down the moment the video starts: it is there to
    // be read before play, not to sit over the picture during it. The mark in
    // the control row stays, so the offer is still one press away.
    if (playing && badge) badge.hidden = true;

    if (timer !== null) { window.clearInterval(timer); timer = null; }
    if (!playing) return;

    let last = performance.now();
    timer = window.setInterval(() => {
      const now = performance.now();
      // A throttled tab hands back a whole second at once; cap the step so a
      // backgrounded section resumes rather than lurching over a whole segment.
      const seconds = Math.min((now - last) / 1000, 0.25);
      last = now;

      const index = segmentAt(elapsed);
      const segment = CUT[index];
      const end = STARTS[index] + segment.seconds;

      if (willJump(index)) {
        // A seek, exactly as the extension does it to a real video.
        if (segment.kind === 'exercise') showSkip(index);
        elapsed = end;
      } else if (segment.kind === 'rest') {
        // Run the rest so it takes the chosen number of REAL seconds.
        elapsed = Math.min(elapsed + seconds * (segment.seconds / restSeconds(segment)), end);
      } else {
        elapsed = Math.min(elapsed + seconds * RATE_SKIM, end);
      }

      if (elapsed >= TOTAL) { elapsed = 0; hideNotice(); }
      paint();
    }, 1000 / 30);
  }
  cleanups.push(() => { if (timer !== null) window.clearInterval(timer); });

  const isPlaying = () => timer !== null;
  if (poster) on(poster, 'click', () => setPlaying(true));
  if (toggle) on(toggle, 'click', () => setPlaying(!isPlaying()));

  if (restSkip) {
    on(restSkip, 'click', (event) => {
      event.stopPropagation();
      const index = segmentAt(elapsed);
      // The button only shows during a rest, but it should not be able to jump
      // an exercise if it is ever reached another way.
      if (CUT[index].kind !== 'rest') return;
      elapsed = STARTS[index] + CUT[index].seconds;
      paint();
    });
  }

  // ─── Preferences ───
  demoEl.querySelectorAll<HTMLButtonElement>('.fc-control-panel__option').forEach((option) => {
    on(option, 'click', () => {
      option.closest('.fc-control-panel__segmented')
        ?.querySelectorAll('[role="radio"]')
        .forEach((sibling) => sibling.setAttribute('aria-checked', String(sibling === option)));
      restChoice = option.dataset.fcRest ?? DEFAULT_REST;
      paint();
    });
  });

  demoEl.querySelectorAll<HTMLButtonElement>('.fc-chip').forEach((chip) => {
    on(chip, 'click', () => {
      const pressed = chip.getAttribute('aria-pressed') === 'true';
      chip.setAttribute('aria-pressed', String(!pressed));
      const set = chip.dataset.fcEquipment ? excludedEquipment : excludedMoves;
      const value = chip.dataset.fcEquipment ?? chip.dataset.fcMove ?? '';
      if (pressed) set.delete(value); else set.add(value);
      syncCounts();
      paint();
    });
  });

  // ─── The offer ───
  //  Mirrors the extension: closing the card does not dismiss the offer, it
  //  leaves the mark in the control row, and that brings it back.
  const closeBadge = demoEl.querySelector<HTMLButtonElement>('.fc-brand-badge__close');
  const modal = demoEl.querySelector<HTMLElement>('.fc-brand-modal');
  const closeModal = demoEl.querySelector<HTMLButtonElement>('.fc-brand-modal__close');

  /** What opened the offer, so focus can go back to it on close. */
  let offerTrigger: HTMLElement | null = null;

  if (badge && mini) {
    mini.hidden = false;
    const showBadge = (show: boolean) => { badge.hidden = !show; };

    const openOffer = (): void => {
      if (!modal) { showBadge(true); return; }
      modal.hidden = false;
      // Opening the offer retires the card: it exists to get the viewer here.
      showBadge(false);
      closeModal?.focus();
    };

    if (closeBadge) on(closeBadge, 'click', () => showBadge(false));
    on(mini, 'click', (event) => {
      event.stopPropagation();
      offerTrigger = mini;
      openOffer();
    });
    demoEl.querySelectorAll<HTMLElement>('.fc-brand-badge__title, .fc-brand-badge__cta')
      .forEach((trigger) => on(trigger, 'click', () => {
        // The card is about to close, so send focus back to the mark instead.
        offerTrigger = mini;
        openOffer();
      }));

    if (modal && closeModal) {
      const panel = modal.querySelector<HTMLElement>('.fc-brand-modal__panel');

      // Focus returns where it came from. Without this the viewer is dropped at
      // the top of the document after closing, which is worse than not moving
      // focus at all.
      const dismiss = () => {
        modal.hidden = true;
        offerTrigger?.focus();
        offerTrigger = null;
      };

      /** Everything inside the panel a keyboard can reach right now. */
      const reachable = (): HTMLElement[] => [
        ...(panel ?? modal).querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((el) => el.offsetParent !== null);

      on(closeModal, 'click', dismiss);
      on(modal, 'click', (event) => { if (event.target === modal) dismiss(); });
      on(document, 'keydown', (event) => {
        const key = event as KeyboardEvent;
        if (modal.hidden) return;
        if (key.key === 'Escape') { dismiss(); return; }
        if (key.key !== 'Tab') return;

        // The panel says aria-modal, so Tab has to honour it rather than walk
        // out into the page behind.
        const items = reachable();
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement as HTMLElement | null;
        const inside = active !== null && (panel ?? modal).contains(active);

        if (key.shiftKey && (!inside || active === first)) {
          key.preventDefault();
          last.focus();
        } else if (!key.shiftKey && (!inside || active === last)) {
          key.preventDefault();
          first.focus();
        }
      });
    }
  }

  syncCounts();
  paint();
  return () => cleanups.forEach((fn) => fn());
}
